import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import {
  FiX,
  FiPlay,
  FiCheckCircle,
  FiSearch,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import UnifiedTaskProgress from '../../../components/UnifiedTaskProgress';
import api from '../../../services/api';
import { tokens as T, surface, hairline } from '../../../styles/designTokens';
import { tokens as activity } from '../../activity-logs/styles';
import { SANS } from '../styles';
import { Capsule, PrimaryCapsule, RoundButton } from '../components/ControlBar';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const COMPANY_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];
const TIPOS = [
  { id: 'icms', label: 'ICMS', color: '#3b82f6' },
  { id: 'ipi', label: 'IPI', color: '#8b5cf6' },
  { id: 'pisCofins', label: 'PIS/COFINS', color: '#10b981' },
];

function lastDayOfMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function toISO(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// "2025-03-01" → "Mar/2025" (lido do texto, sem fuso)
function formatMonthISO(iso) {
  if (!iso) return '';
  const [y, m] = iso.split('-');
  return `${MESES[Number(m) - 1]}/${y}`;
}

function formatCnpj(cnpj) {
  const d = String(cnpj || '').replace(/\D/g, '');
  if (d.length !== 14) return cnpj || '—';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

const initials = (name) => String(name || '')
  .replace(/\b(LTDA|S\.?A\.?|EIRELI|ME|EPP)\b/gi, '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w[0])
  .join('')
  .toUpperCase();

/**
 * Linha de data no estilo do iOS: rótulo à esquerda, valor em cápsula à direita.
 * Tocar abre a grade de meses logo abaixo (como o seletor inline do Ajustes).
 * mode="start" grava o 1º dia do mês; mode="end", o último.
 */
function MonthRow({ label, value, onChange, mode, open, onToggle, min, max }) {
  const today = new Date();
  const ceiling = today.getFullYear() + 1;
  const floor = 2019;
  const [viewYear, setViewYear] = useState(value ? Number(value.slice(0, 4)) : today.getFullYear());

  useEffect(() => {
    if (value) setViewYear(Number(value.slice(0, 4)));
  }, [value]);

  const selKey = value ? value.slice(0, 7) : '';
  const pick = (month) => {
    const day = mode === 'end' ? lastDayOfMonth(viewYear, month) : 1;
    onChange(toISO(viewYear, month, day));
  };

  return (
    <>
      <Row as="button" type="button" onClick={onToggle} aria-expanded={open}>
        <span className="r-main"><span className="r-title">{label}</span></span>
        <ValuePill $set={!!value} $open={open}>
          {value ? formatMonthISO(value) : mode === 'start' ? 'Desde o início' : 'Até o fim'}
        </ValuePill>
        {value && (
          <ClearMini
            role="button"
            tabIndex={0}
            aria-label={`Limpar ${label.toLowerCase()}`}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(null); } }}
          >
            <FiX />
          </ClearMini>
        )}
      </Row>
      {open && (
        <InlinePicker>
          <div className="year">
            <NavBtn type="button" onClick={() => setViewYear((y) => y - 1)} disabled={viewYear <= floor} aria-label="Ano anterior"><FiChevronLeft /></NavBtn>
            <span>{viewYear}</span>
            <NavBtn type="button" onClick={() => setViewYear((y) => y + 1)} disabled={viewYear >= ceiling} aria-label="Próximo ano"><FiChevronRight /></NavBtn>
          </div>
          <div className="grid">
            {MESES.map((m, i) => {
              const key = `${viewYear}-${String(i + 1).padStart(2, '0')}`;
              const disabled = (min && key < min.slice(0, 7)) || (max && key > max.slice(0, 7));
              return (
                <MonthBtn key={m} type="button" $on={key === selKey} disabled={!!disabled} onClick={() => pick(i)}>
                  <span>{m}</span>
                </MonthBtn>
              );
            })}
          </div>
        </InlinePicker>
      )}
    </>
  );
}

/**
 * Modal de Análise de Créditos
 * - Etapa 1: período, empresas, tipos de imposto e parâmetros
 * - Etapa 2: progresso da análise via WebSocket
 * - preview: aberto pelo tutorial. Nada responde, nem pelo teclado: a análise
 *   substitui os créditos do projeto e não pode sair de uma demonstração.
 */
export default function AnaliseCreditsModal({ isOpen, onClose, onComplete, preview = false }) {
  // Período (ISO: dataInicio = 1º dia do mês, dataFim = último dia do mês)
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);
  const [openPicker, setOpenPicker] = useState(null);
  const [tipos, setTipos] = useState(['icms', 'ipi', 'pisCofins']);
  // Ata da reunião (Diogo): alíquota de 4% para produtos importados nas
  // operações interestaduais — parametro ajustavel, default legal 4%.
  const [aliquotaImportados, setAliquotaImportados] = useState('4');

  // Empresas
  const [empresas, setEmpresas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [busca, setBusca] = useState('');
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [erroEmpresas, setErroEmpresas] = useState(false);
  const [empresasKey, setEmpresasKey] = useState(0);

  // Processamento
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [taskProgress, setTaskProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [taskLogs, setTaskLogs] = useState([]);
  const [taskStats, setTaskStats] = useState([]);
  const [outputId, setOutputId] = useState(null);
  const [, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const wsRef = useRef(null);

  // Reset ao fechar
  useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
      setProcessingComplete(false);
      setTaskProgress(0);
      setCurrentStage('');
      setTaskLogs([]);
      setTaskStats([]);
      setOutputId(null);
      setSummary(null);
      setOpenPicker(null);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    }
  }, [isOpen]);

  // Buscar empresas do projeto (mesma fonte do modal de filtros da Reforma)
  useEffect(() => {
    if (!isOpen) return;

    const fetchEmpresas = async () => {
      setLoadingEmpresas(true);
      setErroEmpresas(false);
      try {
        const projectId = localStorage.getItem('vidal_selected_project');
        if (!projectId) { setEmpresas([]); return; }
        const resp = await api.get(`/certificates/list/${projectId}`);
        const certs = resp?.data?.data || resp?.data?.certificates || [];
        setEmpresas(
          certs.map((c, i) => ({
            cnpj: String(c.cnpj || '').replace(/\D/g, ''),
            nome: c.razaoSocial || c.nome || formatCnpj(c.cnpj),
            color: COMPANY_COLORS[i % COMPANY_COLORS.length],
          }))
        );
      } catch (e) {
        console.error('[AnaliseCredits] Erro ao buscar empresas:', e);
        setErroEmpresas(true);
      } finally {
        setLoadingEmpresas(false);
      }
    };
    fetchEmpresas();
  }, [isOpen, empresasKey]);

  const empresasFiltradas = useMemo(() => {
    const term = busca.trim().toLowerCase();
    const digits = term.replace(/\D/g, '');
    return empresas.filter((e) => !term || e.nome.toLowerCase().includes(term) || (digits && e.cnpj.includes(digits)));
  }, [empresas, busca]);

  const toggleEmpresa = (cnpj) => {
    setSelecionadas((prev) => (prev.includes(cnpj) ? prev.filter((c) => c !== cnpj) : [...prev, cnpj]));
  };

  const todasSelecionadas = empresas.length > 0 && selecionadas.length === empresas.length;
  const toggleTodas = () => {
    setSelecionadas(todasSelecionadas ? [] : empresas.map((e) => e.cnpj));
  };

  // Conectar WebSocket quando inicia processamento
  useEffect(() => {
    if (!isProcessing || !outputId) return undefined;

    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    const wsUrl = `wss://log.vidal-app.com/ws?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[AnaliseCredits] WebSocket conectado');
      const projeto = localStorage.getItem('vidal_selected_project');
      // Subscribe ao canal de créditos
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'calculate_credits',
        projectId: projeto,
      }));
    };

    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.success && parsed.message) {
          const msg = parsed.message;

          // Verificar se é do tipo calculate_credits e do outputId correto
          if (msg.type === 'calculate_credits' && msg.data) {
            const { data } = msg;

            if (data.outputId && data.outputId !== outputId) return;

            if (data.progress !== undefined) setTaskProgress(data.progress);
            if (data.stage) setCurrentStage(data.stage);

            if (data.message) {
              let logType = 'progress';
              if (data.status === 'error') logType = 'error';
              else if (data.status === 'completed') logType = 'success';

              setTaskLogs((prev) => [...prev, {
                message: data.message,
                type: logType,
                timestamp: new Date().toLocaleTimeString(),
                progress: data.progress,
              }]);
            }

            if (data.status === 'completed') {
              setProcessingComplete(true);
              setIsProcessing(false);
              setSummary(data.summary || null);

              if (data.summary) {
                setTaskStats([
                  { label: 'Créditos Encontrados', value: data.summary.totalCredits || 0, type: 'success' },
                  { label: 'Valor Total', value: `R$ ${(data.summary.totalValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, type: 'info' },
                  { label: 'Indexados', value: data.summary.indexed || 0, type: 'success' },
                  { label: 'Erros', value: data.summary.errors || 0, type: data.summary.errors > 0 ? 'error' : 'info' },
                ]);
              }
            }

            if (data.status === 'error') setIsProcessing(false);
          }
        }
      } catch (e) {
        console.error('[AnaliseCredits] Erro ao parsear WS:', e);
      }
    };

    ws.onclose = () => {
      console.log('[AnaliseCredits] WebSocket desconectado');
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [isProcessing, outputId]);

  const toggleTipo = (tipo) => {
    setTipos((prev) => (prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo]));
  };

  const handleStartAnalysis = async () => {
    if (preview) return;
    if (tipos.length === 0) {
      toast.error('Selecione ao menos um tipo de imposto');
      return;
    }

    const projeto = localStorage.getItem('vidal_selected_project');
    if (!projeto) {
      toast.error('Nenhum projeto selecionado');
      return;
    }

    setSubmitting(true);
    setIsProcessing(true);
    setTaskProgress(0);
    setTaskLogs([]);
    setTaskStats([]);
    setCurrentStage('Iniciando...');

    try {
      const response = await api.post('/creditos/analisar', {
        projeto,
        startDate: dataInicio || undefined,
        endDate: dataFim || undefined,
        tipos,
        empresas: selecionadas.length > 0 ? selecionadas : undefined,
        aliquotaImportados: aliquotaImportados !== '' ? Number(aliquotaImportados) : undefined,
      });

      if (response.data.success) {
        setOutputId(response.data.outputId);
        setTaskLogs([{
          message: 'Análise enviada para processamento',
          type: 'info',
          timestamp: new Date().toLocaleTimeString(),
        }]);
      } else {
        setIsProcessing(false);
        toast.error(response.data.message || 'Erro ao iniciar análise');
      }
    } catch (error) {
      console.error('Erro ao iniciar análise:', error);
      setIsProcessing(false);
      toast.error('Erro ao iniciar análise de créditos');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;
    if (processingComplete && onComplete) onComplete();
    onClose();
  };

  // Esc fecha, exceto durante o processamento
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (openPicker) { setOpenPicker(null); return; }
      handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const showForm = !isProcessing && !processingComplete;
  const showProgress = isProcessing || processingComplete;

  // Resumo do que será analisado, lido no rodapé antes de confirmar
  let periodo = 'Todo o período';
  if (dataInicio && dataFim) periodo = `${formatMonthISO(dataInicio)} – ${formatMonthISO(dataFim)}`;
  else if (dataInicio) periodo = `Desde ${formatMonthISO(dataInicio)}`;
  else if (dataFim) periodo = `Até ${formatMonthISO(dataFim)}`;
  let empresasTxt = 'todas as empresas';
  if (selecionadas.length > 0 && !todasSelecionadas) {
    empresasTxt = `${selecionadas.length} ${selecionadas.length === 1 ? 'empresa' : 'empresas'}`;
  }
  const tiposTxt = TIPOS.filter((t) => tipos.includes(t.id)).map((t) => t.label).join(', ') || 'nenhum imposto';

  return createPortal(
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <Sheet ref={(node) => { if (node) node.inert = preview; }} role="dialog" aria-modal="true" aria-labelledby="analise-title">
        <Header>
          <HeaderIcon><FiPlay /></HeaderIcon>
          <div className="h-text">
            <h2 id="analise-title">Análise de créditos</h2>
            <p>Identifica créditos tributários não aproveitados nos documentos fiscais do projeto.</p>
          </div>
          <RoundButton type="button" onClick={handleClose} disabled={isProcessing} aria-label="Fechar" title="Fechar">
            <FiX />
          </RoundButton>
        </Header>

        <Body>
          {showForm && (
            <>
              {/* Período */}
              <div data-tour="analise-periodo">
              <GroupLabel>Período</GroupLabel>
              <Card>
                <MonthRow
                  label="Início"
                  mode="start"
                  value={dataInicio}
                  max={dataFim}
                  onChange={(v) => { setDataInicio(v); if (v) setOpenPicker(null); }}
                  open={openPicker === 'start'}
                  onToggle={() => setOpenPicker((p) => (p === 'start' ? null : 'start'))}
                />
                <MonthRow
                  label="Fim"
                  mode="end"
                  value={dataFim}
                  min={dataInicio}
                  onChange={(v) => { setDataFim(v); if (v) setOpenPicker(null); }}
                  open={openPicker === 'end'}
                  onToggle={() => setOpenPicker((p) => (p === 'end' ? null : 'end'))}
                />
              </Card>
              <Footnote>Em branco, analisa todo o período disponível. O início conta do 1º dia do mês, e o fim, até o último.</Footnote>
              </div>

              {/* Empresas */}
              <div data-tour="analise-empresas">
              <GroupLabel>
                <span>Empresas{empresas.length > 0 ? ` · ${selecionadas.length} de ${empresas.length}` : ''}</span>
                {empresas.length > 0 && (
                  <LinkBtn type="button" onClick={toggleTodas}>{todasSelecionadas ? 'Limpar seleção' : 'Selecionar todas'}</LinkBtn>
                )}
              </GroupLabel>
              <Card>
                {empresas.length > 5 && (
                  <SearchWrap>
                    <Search>
                      <FiSearch />
                      <input
                        type="text"
                        placeholder="Buscar empresa ou CNPJ"
                        aria-label="Buscar empresa"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                      />
                      {busca && <button type="button" aria-label="Limpar busca" onClick={() => setBusca('')}><FiX /></button>}
                    </Search>
                  </SearchWrap>
                )}
                <CompanyScroll>
                  {loadingEmpresas && [0, 1, 2].map((i) => (
                    <Row key={i} as="div"><SkeletonLine style={{ width: `${60 - i * 12}%` }} /></Row>
                  ))}
                  {!loadingEmpresas && erroEmpresas && (
                    <EmptyLine>
                      Não foi possível carregar as empresas do projeto.{' '}
                      <LinkBtn type="button" onClick={() => setEmpresasKey((k) => k + 1)}>Tentar de novo</LinkBtn>
                    </EmptyLine>
                  )}
                  {!loadingEmpresas && !erroEmpresas && empresasFiltradas.length === 0 && (
                    <EmptyLine>{empresas.length === 0 ? 'Nenhuma empresa com certificado no projeto' : `Nada encontrado para "${busca}"`}</EmptyLine>
                  )}
                  {!loadingEmpresas && empresasFiltradas.map((e) => {
                    const sel = selecionadas.includes(e.cnpj);
                    return (
                      <Row key={e.cnpj} as="button" type="button" onClick={() => toggleEmpresa(e.cnpj)} aria-pressed={sel}>
                        <Avatar $color={e.color}>{initials(e.nome) || '?'}</Avatar>
                        <span className="r-main">
                          <span className="r-title">{e.nome}</span>
                          <span className="r-sub">{formatCnpj(e.cnpj)}</span>
                        </span>
                        <CheckCircle $on={sel} aria-hidden="true">{sel && <FiCheck />}</CheckCircle>
                      </Row>
                    );
                  })}
                </CompanyScroll>
              </Card>
              <Footnote>Sem seleção, analisa todas as empresas do projeto{erroEmpresas ? ', e você pode iniciar assim mesmo' : ''}.</Footnote>
              </div>

              {/* Tipos de crédito */}
              <div data-tour="analise-tipos">
              <GroupLabel>Tipos de crédito</GroupLabel>
              <Card>
                {TIPOS.map((t) => {
                  const on = tipos.includes(t.id);
                  return (
                    <Row key={t.id} as="label">
                      <Dot $color={t.color} />
                      <span className="r-main">
                        <span className="r-title">{t.label}</span>
                      </span>
                      <Switch $on={on}>
                        <input type="checkbox" checked={on} onChange={() => toggleTipo(t.id)} aria-label={t.label} />
                        <span />
                      </Switch>
                    </Row>
                  );
                })}
              </Card>
              {tipos.length === 0 && <Footnote $warn>Ative ao menos um imposto para iniciar.</Footnote>}
              </div>

              {/* Parâmetros */}
              <div data-tour="analise-parametros">
              <GroupLabel>Parâmetros</GroupLabel>
              <Card>
                <Row as="label">
                  <span className="r-main">
                    <span className="r-title">Alíquota interestadual de importados</span>
                    <span className="r-sub">Padrão legal: 4%</span>
                  </span>
                  <NumberPill>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      min="0"
                      max="25"
                      value={aliquotaImportados}
                      onChange={(e) => setAliquotaImportados(e.target.value)}
                      aria-label="Alíquota interestadual de importados"
                    />
                    <span>%</span>
                  </NumberPill>
                </Row>
              </Card>
              </div>
            </>
          )}

          {showProgress && (
            <ProgressWrap>
              <UnifiedTaskProgress
                isProcessing={isProcessing}
                processingComplete={processingComplete}
                taskProgress={taskProgress}
                currentStage={currentStage}
                taskStats={taskStats}
                taskLogs={taskLogs}
              />
            </ProgressWrap>
          )}
        </Body>

        {showForm && (
          <Footer data-tour="analise-confirmar">
            <div className="summary">
              <strong>{periodo}</strong>
              <span>{empresasTxt} · {tiposTxt}</span>
            </div>
            <Capsule type="button" onClick={handleClose}>Cancelar</Capsule>
            <PrimaryCapsule type="button" onClick={handleStartAnalysis} disabled={tipos.length === 0 || submitting}>
              <FiPlay /> Iniciar análise
            </PrimaryCapsule>
          </Footer>
        )}

        {processingComplete && (
          <Footer>
            <div className="summary"><strong>Análise concluída</strong><span>Os créditos encontrados já estão na tela.</span></div>
            <PrimaryCapsule type="button" onClick={handleClose}><FiCheckCircle /> Ver créditos</PrimaryCapsule>
          </Footer>
        )}
      </Sheet>
    </Overlay>,
    document.body
  );
}

/* ---------- styled ---------- */

const dk = (theme) => activity.dk(theme);
const label2 = ({ theme }) => activity.label2(theme);
// Fundo agrupado do iOS: a folha é um tom abaixo dos cartões
const sheetBg = ({ theme }) => (dk(theme) ? '#161618' : '#f2f2f7');
const cardBg = ({ theme }) => (dk(theme) ? '#232327' : '#ffffff');
const fieldFill = ({ theme }) => (dk(theme) ? 'rgba(118,118,128,0.24)' : 'rgba(118,118,128,0.12)');

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.985); }
  to { opacity: 1; transform: none; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, ${({ theme }) => (dk(theme) ? 0.55 : 0.32)});
  backdrop-filter: blur(6px) saturate(120%);
  -webkit-backdrop-filter: blur(6px) saturate(120%);
  animation: ${fadeIn} 180ms ease;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Sheet = styled.div`
  width: min(560px, 100%);
  max-height: min(860px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  background: ${sheetBg};
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${rise} 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 14px;

  .h-text { flex: 1; min-width: 0; }
  h2 { margin: 0; font-size: 1.2em; font-weight: 700; letter-spacing: -0.025em; }
  p { margin: 3px 0 0; font-size: 0.82em; line-height: 1.4; color: ${label2}; }
`;

const HeaderIcon = styled.span`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 17px;
  background: ${T.brand};
  svg { margin-left: 2px; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 20px 18px;
  overscroll-behavior: contain;
`;

const GroupLabel = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin: 16px 4px 7px;
  font-size: 0.8em;
  font-weight: 500;
  color: ${label2};
`;

const LinkBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 1em;
  font-weight: 500;
  color: ${T.brand};
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const Card = styled.div`
  border-radius: 14px;
  overflow: hidden;
  background: ${cardBg};
  box-shadow: ${({ theme }) => (dk(theme) ? 'none' : '0 1px 2px rgba(0,0,0,0.04)')};
`;

const Footnote = styled.div`
  margin: 7px 4px 0;
  font-size: 0.74em;
  line-height: 1.4;
  color: ${({ $warn }) => ($warn ? '#ff9f0a' : 'inherit')};
  opacity: ${({ $warn }) => ($warn ? 1 : 0.5)};
`;

// Linha de lista agrupada; o separador começa no texto, como no iOS
const Row = styled.div`
  position: relative;
  width: 100%;
  min-height: 48px;
  padding: 9px 14px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  cursor: ${({ as }) => (as === 'div' ? 'default' : 'pointer')};
  transition: background 120ms ease;

  & + &::before, .inline-picker + &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 14px;
    right: 0;
    height: 1px;
    background: ${({ theme }) => hairline(theme)};
  }
  ${({ as }) => as !== 'div' && css`
    &:hover { background: ${({ theme }) => surface(theme, 1)}; }
    &:active { background: ${({ theme }) => surface(theme, 2)}; }
  `}
  &:focus-visible { outline: none; box-shadow: inset 0 0 0 2px ${T.brandRing}; }

  .r-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
  .r-title { font-size: 0.9em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .r-sub { font-size: 0.74em; color: ${label2}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const ValuePill = styled.span`
  padding: 6px 11px;
  border-radius: 8px;
  font-size: 0.86em;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ $set, $open, theme }) => ($set || $open ? T.brand : activity.label2(theme))};
  background: ${({ $open }) => ($open ? T.brandSoft : fieldFill)};
  transition: background 140ms ease, color 140ms ease;
`;

const ClearMini = styled.span`
  width: 22px;
  height: 22px;
  margin-left: -4px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${label2};
  background: ${fieldFill};
  &:hover { color: ${({ theme }) => theme.text}; }
`;

const InlinePicker = styled.div.attrs({ className: 'inline-picker' })`
  padding: 4px 14px 14px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};

  .year {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 6px 0 8px;
    font-size: 0.92em;
    font-weight: 600;
  }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; }
`;

const NavBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: ${T.brand};
  background: transparent;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${T.brandSoft}; }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const MonthBtn = styled.button`
  height: 36px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.86em;
  font-weight: ${({ $on }) => ($on ? 600 : 500)};
  color: ${({ $on, theme }) => ($on ? '#fff' : theme.text)};
  cursor: pointer;
  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 32px;
    border-radius: 999px;
    background: ${({ $on }) => ($on ? T.brand : 'transparent')};
    transition: background 120ms ease;
  }
  &:hover:not(:disabled) span { background: ${({ $on, theme }) => ($on ? T.brand : surface(theme, 2))}; }
  &:disabled { opacity: 0.28; cursor: default; }
`;

const SearchWrap = styled.div`
  padding: 10px 12px 6px;
`;

const Search = styled.label`
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  background: ${fieldFill};
  color: ${label2};
  svg { flex-shrink: 0; font-size: 14px; }
  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.86em;
    color: ${({ theme }) => theme.text};
    &::placeholder { color: ${label2}; }
  }
  button {
    border: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    cursor: pointer;
    color: ${({ theme }) => (dk(theme) ? '#1c1c1e' : '#fff')};
    background: ${label2};
  }
  &:focus-within { box-shadow: 0 0 0 3px ${T.brandRing}; }
`;

const CompanyScroll = styled.div`
  max-height: 232px;
  overflow-y: auto;
  scrollbar-width: thin;
`;

const Avatar = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72em;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}1f`};
`;

const CheckCircle = styled.span`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #fff;
  background: ${({ $on }) => ($on ? T.brand : 'transparent')};
  box-shadow: ${({ $on, theme }) => ($on ? 'none' : `inset 0 0 0 1.5px ${hairline(theme, true)}`)};
  transition: background 140ms ease, transform 140ms ease;
  transform: ${({ $on }) => ($on ? 'scale(1)' : 'scale(0.94)')};
  svg { stroke-width: 3; }
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 4px ${({ $color }) => `${$color}22`};
  margin: 0 3px;
`;

// Switch do iOS: 51×31, trilho verde quando ligado
const Switch = styled.span`
  position: relative;
  width: 46px;
  height: 28px;
  flex-shrink: 0;

  input {
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
  }
  span {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: ${({ $on, theme }) => ($on ? T.brand : dk(theme) ? 'rgba(120,120,128,0.32)' : 'rgba(120,120,128,0.16)')};
    transition: background 200ms ease;
  }
  span::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15), 0 1px 1px rgba(0, 0, 0, 0.16);
    transform: translateX(${({ $on }) => ($on ? '18px' : '0')});
    transition: transform 220ms cubic-bezier(0.3, 1.4, 0.5, 1);
  }
  input:focus-visible + span { box-shadow: 0 0 0 3px ${T.brandRing}; }
  @media (prefers-reduced-motion: reduce) { span, span::after { transition: none; } }
`;

const NumberPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  background: ${fieldFill};
  color: ${T.brand};
  font-weight: 600;
  font-size: 0.9em;

  input {
    width: 3.2em;
    border: none;
    outline: none;
    background: transparent;
    text-align: right;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    color: inherit;
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }
  &:focus-within { box-shadow: 0 0 0 3px ${T.brandRing}; }
`;

const SkeletonLine = styled.span`
  height: 12px;
  border-radius: 6px;
  background: ${({ theme }) => surface(theme, 2)};
`;

const EmptyLine = styled.div`
  padding: 18px 14px;
  text-align: center;
  font-size: 0.82em;
  color: ${label2};
`;

const ProgressWrap = styled.div`
  padding-top: 4px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px 18px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${sheetBg};

  .summary { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
  .summary strong { font-size: 0.86em; font-weight: 600; letter-spacing: -0.01em; }
  .summary span { font-size: 0.74em; color: ${label2}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  @media (max-width: 480px) {
    flex-wrap: wrap;
    .summary { flex-basis: 100%; }
    button { flex: 1; justify-content: center; }
  }
`;

